import { fromZonedTime } from "date-fns-tz";
import type { CiudadVivaEvent } from "./events";

const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000; // 2h, cuando no hay time.end
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Eventos recurrentes (museos, funciones semanales) o con varias fechas ya
// tienen su horario en texto libre (`schedule`) en la página — agregarlos al
// calendario con una sola fecha sería engañoso, así que el botón no se
// muestra para esos casos.
export function isEligibleForCalendar(event: CiudadVivaEvent): boolean {
  if (event.recurrenceType && event.recurrenceType !== "none") return false;
  if (event.dates && event.dates.length > 1) return false;
  return true;
}

export interface CalendarEventData {
  title: string;
  description: string;
  location: string;
  start: Date;
  end: Date;
  allDay: boolean;
}

export function getCalendarEventData(event: CiudadVivaEvent, pageUrl: string): CalendarEventData {
  const tz = event.timezone ?? "America/Santiago";
  const dateISO = (event.dates && event.dates[0]) ?? event.dateStartISO;
  const day = (dateISO ?? new Date().toISOString()).slice(0, 10);
  const location = [event.venue, event.district].filter(Boolean).join(", ");
  const description = [event.description, pageUrl].filter(Boolean).join("\n\n");

  if (event.timeStart) {
    const start = fromZonedTime(`${day}T${event.timeStart}:00`, tz);
    const end = event.timeEnd
      ? fromZonedTime(`${day}T${event.timeEnd}:00`, tz)
      : new Date(start.getTime() + DEFAULT_DURATION_MS);
    return { title: event.title, description, location, start, end, allDay: false };
  }

  // Sin horario: evento de "todo el día". date.end (si existe) marca el
  // último día inclusive — Google/ICS esperan el end EXCLUSIVO, por eso se
  // suma 1 día.
  const start = fromZonedTime(`${day}T00:00:00`, tz);
  const endDay = (event.dateEndISO ?? dateISO ?? day).slice(0, 10);
  const endInclusive = fromZonedTime(`${endDay}T00:00:00`, tz);
  const end = new Date(endInclusive.getTime() + ONE_DAY_MS);
  return { title: event.title, description, location, start, end, allDay: true };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatUtcDate(d: Date, allDay: boolean): string {
  if (allDay) {
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
  }
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

export function buildGoogleCalendarUrl(event: CiudadVivaEvent, pageUrl: string): string {
  const data = getCalendarEventData(event, pageUrl);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: data.title,
    dates: `${formatUtcDate(data.start, data.allDay)}/${formatUtcDate(data.end, data.allDay)}`,
    details: data.description.slice(0, 1500),
    location: data.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function escapeICS(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

function buildICS(event: CiudadVivaEvent, pageUrl: string): string {
  const data = getCalendarEventData(event, pageUrl);
  const dtStart = data.allDay
    ? `DTSTART;VALUE=DATE:${formatUtcDate(data.start, true)}`
    : `DTSTART:${formatUtcDate(data.start, false)}`;
  const dtEnd = data.allDay
    ? `DTEND;VALUE=DATE:${formatUtcDate(data.end, true)}`
    : `DTEND:${formatUtcDate(data.end, false)}`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Ciudad Viva//Eventos//ES",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${event.slug}@ciudad-viva.cl`,
    `DTSTAMP:${formatUtcDate(new Date(), false)}`,
    dtStart,
    dtEnd,
    `SUMMARY:${escapeICS(data.title)}`,
    `DESCRIPTION:${escapeICS(data.description)}`,
    `LOCATION:${escapeICS(data.location)}`,
    `URL:${pageUrl}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

export function downloadICS(event: CiudadVivaEvent, pageUrl: string): void {
  const ics = buildICS(event, pageUrl);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${event.slug}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
